import { alpha, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { DatePicker } from '@mui/x-date-pickers'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useBasePath } from 'components/Providers'
import { searchLogs } from 'store/mentorship/log'
import NoRecords from 'components/NoRecords'
import dayjs from 'dayjs'

const statusConfig = {
  submitted: { color: '#ed6c02', muiColor: 'warning' },
  approved:  { color: '#2e7d32', muiColor: 'success' },
  rejected:  { color: '#d32f2f', muiColor: 'error'   },
}

const formatTime = t => t ? dayjs(t, 'HH:mm:ss').format('h:mm A') : null

export default function MyLogsList({ mentorshipId }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const basePath = useBasePath()
  const list = useSelector(state => state.mentorship.log.list)
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)

  useEffect(() => {
    if (mentorshipId) dispatch(searchLogs({ mentorshipId }))
  }, [dispatch, mentorshipId])

  if (list === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  const filtered = list
    .filter(log => {
      const date = dayjs(log.logDate)
      if (fromDate && date.isBefore(fromDate, 'day')) return false
      if (toDate && date.isAfter(toDate, 'day')) return false
      return true
    })
    .sort((a, b) => dayjs(b.logDate).diff(dayjs(a.logDate)))

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <DatePicker
          label={t('From')}
          value={fromDate}
          onChange={setFromDate}
          slotProps={{
            textField: { size: 'small' },
            field: { clearable: true },
          }}
          sx={{ flex: 1 }}
        />
        <DatePicker
          label={t('To')}
          value={toDate}
          onChange={setToDate}
          slotProps={{
            textField: { size: 'small' },
            field: { clearable: true },
          }}
          sx={{ flex: 1 }}
        />
      </Stack>

      {filtered.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <NoRecords message={t('No logs found')} />
        </Box>
      )}

      {filtered.map(log => {

        const { color, muiColor } = statusConfig[log.status] || { color: '#9e9e9e', muiColor: 'default' }
        const startTime = formatTime(log.startTime)
        const endTime = formatTime(log.endTime)

        return (
          <Paper
            key={log.id}
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              transition: 'box-shadow 0.2s, border-color 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: theme => `0 2px 12px ${alpha(theme.palette.primary.main, 0.12)}`,
              },
            }}
          >
            <Stack direction="row" alignItems="stretch">
              {/* Date badge */}
              <Box
                sx={{
                  width: 72,
                  flexShrink: 0,
                  bgcolor: alpha(color, 0.1),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 2,
                  borderRight: `1px solid ${alpha(color, 0.2)}`,
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight={800}
                  lineHeight={1}
                  sx={{ color }}
                >
                  {dayjs(log.logDate).format('D')}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color, textTransform: 'uppercase', letterSpacing: 1, mt: 0.25 }}
                >
                  {dayjs(log.logDate).format('MMM')}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: alpha(color, 0.7), fontSize: 10, mt: 0.25 }}
                >
                  {dayjs(log.logDate).format('YYYY')}
                </Typography>
              </Box>

              {/* Body */}
              <Stack
                direction="row"
                alignItems="center"
                flexWrap="wrap"
                sx={{ flex: 1, px: { xs: 1.5, sm: 2.5 }, py: 1.5, gap: 1 }}
              >
                <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 } }}>
                  <Typography variant="body2" fontWeight={600}>
                    {dayjs(log.logDate).format('dddd')}
                  </Typography>
                  {startTime || endTime ? (
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.5 }}>
                      <AccessTimeIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                      <Typography variant="caption" color="text.secondary">
                        {[startTime, endTime].filter(Boolean).join(' – ')}
                      </Typography>
      
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      {t('No time recorded')}
                    </Typography>
                  )}
                  <Typography variant='body2'>
                    {log.content}
                  </Typography>
                </Box>

                <Chip
                  label={t(log.status?.toUpperCase())}
                  color={muiColor}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />

                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigate(`${basePath}/${log.id}`)}
                  sx={{ minWidth: 72, flexShrink: 0 }}
                >
                  {t('More')}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )
      })}
    </Stack>
  )
}
